// ✅ src/components/screens/PaymentsDeliveryScreen.js (FULL - IN-APP SELECT FIXED)
// ✅ Replaces native <select> popups with in-app bottom-sheet selector (AppSelect)
// Collections:
// - stores/{uid}/paymentMethods
// - stores/{uid}/deliveryOptions

import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function parseAreas(s) {
  return safe(s)
    .split(",")
    .map((x) => safe(x))
    .filter(Boolean)
    .slice(0, 30);
}

/* ---------------- In-app Select (Bottom Sheet) ---------------- */
function AppSelect({ value, onChange, options, placeholder = "Select...", title = "Select" }) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef(null);

  const current = options.find((o) => String(o.value) === String(value));
  const label = current?.label || placeholder;

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <button type="button" className="pd-selectBtn" onClick={() => setOpen(true)}>
        <span className={`pd-selectLabel ${current ? "" : "muted"}`}>{label}</span>
        <span className="pd-selectChevron">▾</span>
      </button>

      {open ? (
        <div
          className="pd-sheetOverlay"
          onClick={(e) => {
            // close only if user clicks overlay (outside sheet)
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="pd-sheet" ref={sheetRef}>
            <div className="pd-sheetTop">
              <div className="pd-sheetTitle">{title}</div>
              <button type="button" className="pd-sheetClose" onClick={close}>
                ✕
              </button>
            </div>

            <div className="pd-sheetList">
              {options.map((o) => {
                const active = String(o.value) === String(value);
                return (
                  <button
                    type="button"
                    key={String(o.value)}
                    className={`pd-sheetItem ${active ? "active" : ""}`}
                    onClick={() => {
                      onChange(o.value);
                      close();
                    }}
                  >
                    <div className="pd-sheetItemLabel">{o.label}</div>
                    {o.sub ? <div className="pd-sheetItemSub">{o.sub}</div> : null}
                    {active ? <div className="pd-sheetTick">✓</div> : null}
                  </button>
                );
              })}
            </div>

            <div className="pd-sheetSafeArea" />
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function PaymentsDeliveryScreen({ goBack }) {
  const [user, setUser] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [payments, setPayments] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  // Payments form
  const [payForm, setPayForm] = useState({
    type: "MOMO",
    label: "",
    network: "MTN",
    number: "",
    name: "",
    bankName: "",
    accountNumber: "",
  });

  // Delivery form
  const [delForm, setDelForm] = useState({
    type: "DELIVERY",
    title: "",
    pricingMode: "FIXED", // FIXED | QUOTE
    fee: "",
    etaText: "",
    areasText: "",
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;

    let alive = true;
    const unsubs = [];
    setLoading(true);
    setMsg("");

    const payQ = query(
      collection(db, "stores", user.uid, "paymentMethods"),
      orderBy("createdAt", "desc")
    );
    const delQ = query(
      collection(db, "stores", user.uid, "deliveryOptions"),
      orderBy("createdAt", "desc")
    );

    unsubs.push(
      onSnapshot(
        payQ,
        (qs) =>
          alive &&
          setPayments(qs.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
        (err) => alive && setMsg(err?.message || "Failed to load payment methods.")
      )
    );

    unsubs.push(
      onSnapshot(
        delQ,
        (qs) =>
          alive &&
          setDeliveries(qs.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
        (err) => alive && setMsg(err?.message || "Failed to load delivery options.")
      )
    );

    setLoading(false);

    return () => {
      alive = false;
      unsubs.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
    };
  }, [user]);

  /* ---------------- Payments ---------------- */
  const addPayment = async () => {
    if (!user) return;

    const type = safe(payForm.type).toUpperCase();
    const label =
      safe(payForm.label) ||
      (type === "MOMO" ? "MoMo" : type === "BANK" ? "Bank" : "Cash");

    if (type === "MOMO" && safe(payForm.number).length < 8) {
      setMsg("MoMo number is required.");
      return;
    }
    if (type === "BANK" && safe(payForm.accountNumber).length < 6) {
      setMsg("Account number is required.");
      return;
    }

    try {
      setMsg("");

      const details =
        type === "MOMO"
          ? {
              network: safe(payForm.network).toUpperCase() || "MTN",
              number: safe(payForm.number),
              name: safe(payForm.name),
            }
          : type === "BANK"
          ? {
              bankName: safe(payForm.bankName),
              accountNumber: safe(payForm.accountNumber),
              name: safe(payForm.name),
            }
          : { note: "Pay on pickup / meetup" };

      await addDoc(collection(db, "stores", user.uid, "paymentMethods"), {
        type,
        label,
        isEnabled: true,
        details,
        iconUrl: "",
        rightText: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setPayForm({
        type: "MOMO",
        label: "",
        network: "MTN",
        number: "",
        name: "",
        bankName: "",
        accountNumber: "",
      });

      setMsg("✅ Payment method added.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to add payment method.");
    }
  };

  const togglePayment = async (p) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, "stores", user.uid, "paymentMethods", p.id), {
        isEnabled: !(p?.isEnabled !== false),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      setMsg(e?.message || "Failed to update payment.");
    }
  };

  const removePayment = async (p) => {
    if (!user) return;
    const ok = window.confirm("Delete this payment method?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "stores", user.uid, "paymentMethods", p.id));
      setMsg("✅ Deleted.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to delete.");
    }
  };

  /* ---------------- Delivery ---------------- */
  const addDelivery = async () => {
    if (!user) return;

    const type = safe(delForm.type).toUpperCase();
    const pricingMode = safe(delForm.pricingMode).toUpperCase() || "FIXED";

    const title =
      safe(delForm.title) ||
      (type === "PICKUP"
        ? "Local Pickup"
        : type === "MEETUP"
        ? "Meet-up Location"
        : "Delivery");

    const feeNum = Number(delForm.fee || 0);
    const fee = Number.isNaN(feeNum) ? 0 : feeNum;

    const areas = parseAreas(delForm.areasText);
    const finalFee = pricingMode === "QUOTE" ? 0 : fee;

    try {
      setMsg("");
      await addDoc(collection(db, "stores", user.uid, "deliveryOptions"), {
        type,
        title,
        pricingMode,
        fee: finalFee,
        etaText: safe(delForm.etaText),
        areas,
        isEnabled: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setDelForm({
        type: "DELIVERY",
        title: "",
        pricingMode: "FIXED",
        fee: "",
        etaText: "",
        areasText: "",
      });

      setMsg("✅ Delivery option added.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to add delivery option.");
    }
  };

  const toggleDelivery = async (d) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, "stores", user.uid, "deliveryOptions", d.id), {
        isEnabled: !(d?.isEnabled !== false),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      setMsg(e?.message || "Failed to update delivery.");
    }
  };

  const removeDelivery = async (d) => {
    if (!user) return;
    const ok = window.confirm("Delete this delivery option?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "stores", user.uid, "deliveryOptions", d.id));
      setMsg("✅ Deleted.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to delete.");
    }
  };

  const enabledPayments = useMemo(
    () => payments.filter((p) => p?.isEnabled !== false),
    [payments]
  );
  const enabledDeliveries = useMemo(
    () => deliveries.filter((d) => d?.isEnabled !== false),
    [deliveries]
  );

  const paymentTypeOptions = [
    { value: "MOMO", label: "MoMo" },
    { value: "BANK", label: "Bank" },
    { value: "CASH", label: "Cash" },
  ];

  const momoNetworkOptions = [
    { value: "MTN", label: "MTN" },
    { value: "TELECEL", label: "Telecel" },
    { value: "AIRTELTIGO", label: "AirtelTigo" },
  ];

  const deliveryTypeOptions = [
    { value: "DELIVERY", label: "Delivery" },
    { value: "PICKUP", label: "Local Pickup" },
    { value: "MEETUP", label: "Meet-up Location" },
  ];

  const pricingModeOptions = [
    { value: "FIXED", label: "Fixed fee" },
    { value: "QUOTE", label: "Ask for quote (fee decided later)" },
  ];

  return (
    <div className="pd-page">
      <style>{css}</style>

      <div className="pd-wrap">
        <div className="pd-top">
          <button className="pd-back" onClick={goBack}>
            ← Back
          </button>
          <div className="pd-title">Payments & Delivery</div>
          <div style={{ width: 70 }} />
        </div>

        {msg ? (
          <div className={`pd-msg ${msg.includes("✅") ? "" : "is-err"}`}>{msg}</div>
        ) : null}

        {loading ? <div className="pd-card">Loading…</div> : null}

        <div className="pd-grid">
          {/* Payments */}
          <div className="pd-card">
            <div className="pd-card-title">Payment Methods</div>

            <div className="pd-form">
              <AppSelect
                title="Payment Type"
                value={payForm.type}
                onChange={(v) => setPayForm((s) => ({ ...s, type: v }))}
                options={paymentTypeOptions}
              />

              <input
                className="pd-input"
                placeholder="Label (optional) e.g. MTN MoMo"
                value={payForm.label}
                onChange={(e) => setPayForm((s) => ({ ...s, label: e.target.value }))}
              />

              {safe(payForm.type).toUpperCase() === "MOMO" ? (
                <>
                  <AppSelect
                    title="MoMo Network"
                    value={payForm.network}
                    onChange={(v) => setPayForm((s) => ({ ...s, network: v }))}
                    options={momoNetworkOptions}
                  />

                  <input
                    className="pd-input"
                    placeholder="MoMo Number"
                    value={payForm.number}
                    onChange={(e) => setPayForm((s) => ({ ...s, number: e.target.value }))}
                  />

                  <input
                    className="pd-input"
                    placeholder="MoMo Name (optional) e.g. Ama Mensah"
                    value={payForm.name}
                    onChange={(e) => setPayForm((s) => ({ ...s, name: e.target.value }))}
                  />
                </>
              ) : null}

              {safe(payForm.type).toUpperCase() === "BANK" ? (
                <>
                  <input
                    className="pd-input"
                    placeholder="Bank Name"
                    value={payForm.bankName}
                    onChange={(e) => setPayForm((s) => ({ ...s, bankName: e.target.value }))}
                  />
                  <input
                    className="pd-input"
                    placeholder="Account Number"
                    value={payForm.accountNumber}
                    onChange={(e) =>
                      setPayForm((s) => ({ ...s, accountNumber: e.target.value }))
                    }
                  />
                  <input
                    className="pd-input"
                    placeholder="Account Name (optional)"
                    value={payForm.name}
                    onChange={(e) => setPayForm((s) => ({ ...s, name: e.target.value }))}
                  />
                </>
              ) : null}

              {safe(payForm.type).toUpperCase() === "CASH" ? (
                <div className="pd-hint">
                  Cash means pay in person (pickup/meetup/delivery). Add details in your chat
                  with buyers.
                </div>
              ) : null}

              <button className="pd-primary" onClick={addPayment}>
                Add Payment
              </button>
            </div>

            <div className="pd-list">
              {payments.length === 0 ? (
                <div className="pd-empty">No payment methods yet.</div>
              ) : (
                payments.map((p) => {
                  const type = safe(p.type).toUpperCase() || "—";
                  const label = safe(p.label) || type;
                  const enabled = p?.isEnabled !== false;

                  const d = p?.details || {};
                  const details =
                    type === "MOMO"
                      ? `MoMo: ${safe(d?.network) || "—"} • ${safe(d?.number) || "—"}${
                          safe(d?.name) ? ` • ${safe(d?.name)}` : ""
                        }`
                      : type === "BANK"
                      ? `Bank: ${safe(d?.bankName) || "—"} • ${safe(d?.accountNumber) || "—"}${
                          safe(d?.name) ? ` • ${safe(d?.name)}` : ""
                        }`
                      : "Cash / In-person";

                  return (
                    <div className="pd-item" key={p.id}>
                      <div className="pd-item-left">
                        <div className="pd-item-title">{label}</div>
                        <div className="pd-item-sub">{details}</div>
                      </div>

                      <div className="pd-actions">
                        <button className="pd-chip" onClick={() => togglePayment(p)}>
                          {enabled ? "Enabled" : "Disabled"}
                        </button>
                        <button className="pd-chip danger" onClick={() => removePayment(p)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="pd-note">
              Enabled: <b>{enabledPayments.length}</b>
            </div>
          </div>

          {/* Delivery */}
          <div className="pd-card">
            <div className="pd-card-title">Delivery Options (Zones)</div>

            <div className="pd-form">
              <AppSelect
                title="Delivery Type"
                value={delForm.type}
                onChange={(v) => setDelForm((s) => ({ ...s, type: v }))}
                options={deliveryTypeOptions}
              />

              <input
                className="pd-input"
                placeholder="Title e.g. Accra / Tema, Eastern Region, Kumasi"
                value={delForm.title}
                onChange={(e) => setDelForm((s) => ({ ...s, title: e.target.value }))}
              />

              <AppSelect
                title="Pricing Mode"
                value={delForm.pricingMode}
                onChange={(v) => setDelForm((s) => ({ ...s, pricingMode: v }))}
                options={pricingModeOptions}
              />

              <input
                className="pd-input"
                placeholder={
                  safe(delForm.pricingMode).toUpperCase() === "QUOTE"
                    ? "Fee will be 0 (quote)"
                    : "Fee (GHS) e.g. 20"
                }
                value={delForm.fee}
                disabled={safe(delForm.pricingMode).toUpperCase() === "QUOTE"}
                onChange={(e) => setDelForm((s) => ({ ...s, fee: e.target.value }))}
              />

              <input
                className="pd-input"
                placeholder="ETA (e.g. same day, 1-2 days)"
                value={delForm.etaText}
                onChange={(e) => setDelForm((s) => ({ ...s, etaText: e.target.value }))}
              />

              <input
                className="pd-input"
                placeholder='Covered areas (comma separated) e.g. Accra,Tema,Kasoa or "Kumasi"'
                value={delForm.areasText}
                onChange={(e) => setDelForm((s) => ({ ...s, areasText: e.target.value }))}
              />

              <button className="pd-primary" onClick={addDelivery}>
                Add Delivery Option
              </button>
            </div>

            <div className="pd-list">
              {deliveries.length === 0 ? (
                <div className="pd-empty">No delivery options yet.</div>
              ) : (
                deliveries.map((d) => {
                  const title = safe(d.title) || safe(d.type) || "Delivery";
                  const enabled = d?.isEnabled !== false;
                  const pm = safe(d?.pricingMode).toUpperCase() || "FIXED";
                  const areas = Array.isArray(d?.areas) ? d.areas : [];
                  const areasText = areas.length ? areas.join(", ") : "—";

                  return (
                    <div className="pd-item" key={d.id}>
                      <div className="pd-item-left">
                        <div className="pd-item-title">{title}</div>
                        <div className="pd-item-sub">
                          {pm === "QUOTE"
                            ? `Fee: QUOTE • ETA: ${safe(d.etaText) || "—"}`
                            : `Fee: ${moneyGHS(d.fee)} • ETA: ${safe(d.etaText) || "—"}`}
                        </div>
                        <div className="pd-item-sub">Areas: {areasText}</div>
                      </div>

                      <div className="pd-actions">
                        <button className="pd-chip" onClick={() => toggleDelivery(d)}>
                          {enabled ? "Enabled" : "Disabled"}
                        </button>
                        <button className="pd-chip danger" onClick={() => removeDelivery(d)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="pd-note">
              Enabled: <b>{enabledDeliveries.length}</b>
            </div>
          </div>
        </div>

        <div className="pd-help">
          <b>Quick setup tip:</b>
          <br />
          Add multiple zones like: <b>Accra/Tema (20)</b>, <b>Eastern (50)</b>,{" "}
          <b>Kumasi (90)</b>.
          <br />
          Then add a fallback option: <b>Ask for quote</b> (QUOTE) for far areas.
        </div>
      </div>
    </div>
  );
}

const css = `
.pd-page{
  min-height:100vh;
  background:#f3f6ff;
  padding:16px;
  font-family:Inter,system-ui,Segoe UI,Arial;
  color:#0f172a;
}

.pd-wrap{
  width:100%;
  max-width:1100px;
  margin:0 auto;
  display:grid;
  gap:12px;
}

/* Top bar */
.pd-top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  background:transparent;
}

.pd-back{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
}

.pd-title{
  font-weight:1100;
  font-size:20px;
}

/* Message */
.pd-msg{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:14px;
  padding:12px;
  font-weight:900;
}
.pd-msg.is-err{
  border-color:rgba(255,70,120,0.35);
  background:rgba(255,70,120,0.06);
  color:#b91c1c;
}

/* Desktop grid */
.pd-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.pd-card{
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:14px;
  box-shadow:0 10px 26px rgba(15,23,42,0.06);
  display:grid;
  gap:12px;
}

.pd-card-title{
  font-weight:1100;
  font-size:16px;
}

.pd-form{
  display:grid;
  gap:10px;
  border:1px solid #eef2ff;
  background:#fbfdff;
  border-radius:14px;
  padding:12px;
}

.pd-input{
  width:100%;
  border:1px solid #cbd5e1;
  border-radius:12px;
  padding:12px 12px;
  font-weight:900;
  outline:none;
  background:#fff;
  font-size:14px;
}

.pd-input:disabled{
  opacity:.75;
  background:#f8fafc;
}

.pd-primary{
  border:0;
  background:#2f61c5;
  color:#fff;
  border-radius:12px;
  padding:12px 12px;
  font-weight:1000;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(47,97,197,0.18);
}

/* ✅ In-app select button */
.pd-selectBtn{
  width:100%;
  border:1px solid #cbd5e1;
  border-radius:12px;
  padding:12px 12px;
  font-weight:900;
  outline:none;
  background:#fff;
  font-size:14px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  cursor:pointer;
}
.pd-selectLabel{ min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-selectLabel.muted{ color:#94a3b8; }
.pd-selectChevron{ color:#64748b; font-weight:1100; }

.pd-list{
  display:grid;
  gap:10px;
}

.pd-item{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  border:1px solid #eef2ff;
  background:#fbfdff;
  border-radius:14px;
  padding:12px;
}

.pd-item-left{ min-width:0; }

.pd-item-title{
  font-weight:1100;
  font-size:14px;
}

.pd-item-sub{
  margin-top:6px;
  color:#64748b;
  font-weight:900;
  font-size:12px;
  line-height:1.45;
  word-break:break-word;
}

.pd-actions{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-end;
}

.pd-chip{
  border:1px solid #e7ecf6;
  background:#f7fbff;
  border-radius:999px;
  padding:9px 12px;
  font-weight:1000;
  cursor:pointer;
  font-size:12px;
}

.pd-chip.danger{
  background:#fff1f2;
  border-color:#fecdd3;
  color:#be123c;
}

.pd-empty{
  color:#64748b;
  font-weight:900;
}

.pd-note{
  color:#64748b;
  font-weight:900;
  font-size:12px;
}

.pd-help{
  background:#ffffff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:14px;
  color:#334155;
  font-weight:900;
  font-size:13px;
  line-height:1.6;
}

.pd-hint{
  color:#64748b;
  font-weight:900;
  font-size:12px;
  line-height:1.6;
  padding:10px 12px;
  border:1px dashed #dbe5ff;
  border-radius:12px;
  background:#ffffff;
}

/* ✅ Bottom sheet */
.pd-sheetOverlay{
  position:fixed;
  inset:0;
  background:rgba(2,6,23,0.55);
  z-index:9999;
  display:flex;
  align-items:flex-end;
  justify-content:center;
  padding:10px;
}

.pd-sheet{
  width:100%;
  max-width:560px;
  background:#fff;
  border-radius:18px;
  border:1px solid #e7ecf6;
  box-shadow:0 30px 80px rgba(2,6,23,0.35);
  overflow:hidden;
}

.pd-sheetTop{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:12px 12px;
  border-bottom:1px solid #eef2ff;
}

.pd-sheetTitle{
  font-weight:1100;
  font-size:14px;
}

.pd-sheetClose{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:10px;
  padding:8px 10px;
  cursor:pointer;
  font-weight:1100;
}

.pd-sheetList{
  display:grid;
  max-height:55vh;
  overflow:auto;
}

.pd-sheetItem{
  border:0;
  background:#fff;
  text-align:left;
  padding:12px 12px;
  cursor:pointer;
  border-bottom:1px solid #f1f5f9;
  position:relative;
}

.pd-sheetItem:hover{
  background:#f8fafc;
}

.pd-sheetItem.active{
  background:#eef2ff;
}

.pd-sheetItemLabel{
  font-weight:1100;
  font-size:14px;
}

.pd-sheetItemSub{
  margin-top:4px;
  font-weight:900;
  color:#64748b;
  font-size:12px;
  line-height:1.45;
}

.pd-sheetTick{
  position:absolute;
  right:12px;
  top:12px;
  font-weight:1200;
  color:#1d4ed8;
}

.pd-sheetSafeArea{
  height: env(safe-area-inset-bottom, 0px);
}

/* ✅ Mobile: full width, bigger input, sticky header */
@media (max-width: 520px){
  .pd-page{ padding:12px; }

  .pd-top{
    position:sticky;
    top:0;
    z-index:5;
    padding:10px 0;
    background:#f3f6ff;
  }

  .pd-title{ font-size:18px; }

  .pd-grid{
    grid-template-columns:1fr;
    gap:12px;
  }

  .pd-card{
    padding:14px;
    border-radius:16px;
  }

  .pd-form{
    padding:12px;
  }

  .pd-input,
  .pd-selectBtn{
    padding:14px 12px;
    font-size:16px; /* prevents iOS zoom + looks bigger */
    border-radius:14px;
  }

  .pd-primary{
    padding:14px 12px;
    font-size:16px;
    border-radius:14px;
  }

  .pd-item{
    padding:12px;
  }

  .pd-chip{
    padding:10px 12px;
    font-size:12px;
  }
}

/* Small tablets */
@media (max-width: 920px){
  .pd-grid{ grid-template-columns:1fr; }
}
`;
